import React, { useState } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Settings, Lock, User, Mail, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SettingsPage() {
  const { user, getAuthHeaders, logout } = useAuth();
  const [changingPassword, setChangingPassword] = useState(false);

  // Password change state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    setChangingPassword(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API_URL}/api/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, { headers });
      
      toast.success('Password changed successfully! Please login again.');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      // Logout after password change
      setTimeout(() => logout(), 2000);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to change password';
      toast.error(typeof message === 'string' ? message : 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const getProgramName = (program) => {
    if (program === 'doctor') return 'Doctor of Humanitarian Action & Development';
    if (program === 'mdppm') return 'Master of Development Project & Program Management';
    return 'Master of Humanitarian Action & Development';
  };

  const isVisitor = user?.role === 'visitor';

  return (
    <div className="flex min-h-screen pt-14 lg:pt-0" data-testid="settings-page">
      <Sidebar />
      <main className="flex-1 p-4 sm:p-6 lg:p-8 bg-background overflow-auto">
        {/* Visitor Banner */}
        {isVisitor && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-sm" data-testid="visitor-banner">
            <p className="text-amber-800 text-sm font-medium">You are viewing this page as a visitor. Password changes are disabled.</p>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-light text-primary mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Account Settings
          </h1>
          <p className="text-muted-foreground">
            Manage your account settings and preferences
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-4xl">
          {/* Profile Information */}
          <Card className="border border-border shadow-none" data-testid="profile-info">
            <CardHeader>
              <CardTitle className="text-xl font-medium flex items-center gap-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                <User className="w-5 h-5" />
                Profile Information
              </CardTitle>
              <CardDescription>
                Your account details
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-sm">
                <User className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Full Name</p>
                  <p className="font-medium">{user?.name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-sm">
                <Mail className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Email Address</p>
                  <p className="font-medium">{user?.email}</p>
                </div>
              </div>
              {user?.program && (user?.role === 'student' || user?.role === 'visitor') && (
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-sm">
                  <GraduationCap className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Program</p>
                    <p className="font-medium">{getProgramName(user.program)}</p>
                  </div>
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-4">
                Contact your administrator to update your profile information.
              </p>
            </CardContent>
          </Card>

          {/* Change Password */}
          <Card className="border border-border shadow-none" data-testid="password-change-form">
            <CardHeader>
              <CardTitle className="text-xl font-medium flex items-center gap-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                <Lock className="w-5 h-5" />
                Change Password
              </CardTitle>
              <CardDescription>
                Update your account password. You will be logged out after changing.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="current_password">Current Password</Label>
                  <Input
                    id="current_password"
                    type="password"
                    placeholder="Enter your current password"
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                    required
                    data-testid="current-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new_password">New Password</Label>
                  <Input
                    id="new_password"
                    type="password"
                    placeholder="Enter your new password"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    required
                    data-testid="new-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm_password">Confirm New Password</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    placeholder="Confirm your new password"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={changingPassword || isVisitor}
                  className="w-full"
                  data-testid="change-password-button"
                >
                  {changingPassword ? 'Changing Password...' : 'Change Password'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
